/**
 * Voice & Speech panel for the Ragsmith Web UI.
 *
 * Grew out of the voice picker. One modal now holds everything a listener
 * adjusts about speech: which voice, how fast (Kokoro), how expressive and
 * at what pace (the expressive engine, when a worker offers it), and the
 * words this scope teaches its ear and mouth — the vocabulary Whisper is
 * hinted with and the pronunciations the speaker follows.
 *
 * Per-listener choices (voice, sliders) live in localStorage as the voice
 * always did. The word lists are server state, scoped to the container.
 */
import { API_BASE } from './state.js';
import { getHeaders } from './api.js';
import { state } from './state.js';
import { renderSpeechUrl } from './tts-player.js';
import { initVoiceDesigner, showVoiceDesigner, hideVoiceDesigner } from './voice-designer.js';

const LS_KEY = 'ragsmith_tts_voice';
const LS_SPEED = 'ragsmith_tts_speed';
const LS_EXAG = 'ragsmith_tts_exaggeration';
const LS_PACE = 'ragsmith_tts_pace';
const LS_PITCH = 'ragsmith_tts_pitch';
const LS_VAR = 'ragsmith_tts_variation';

// Kokoro's default, used when a worker is speaking. The cloud lane has its
// own default (Matilda), which the server applies — the client never needs
// to name it, since getSelectedVoice() falls back to whatever is available.
const DEFAULT_VOICE = 'af_heart';

// Bounds mirror TTS_OPTION_BOUNDS on the server; the server clamps anyway.
// Every range is symmetric about its default, so an untouched slider sits in
// the middle of its track and five neutral handles line up in a column.
// Speed ran 0.6 to 1.6 and pace 0.1 to 1.0, which put "normal" at 40% and
// 44% of the way along, next to three sliders resting at the centre.
const SLIDERS = {
    speed:        { min: 0.5, max: 1.5, step: 0.05, def: 1.0, ls: LS_SPEED, engine: 'both',       label: 'Speed' },
    exaggeration: { min: 0.0, max: 1.0, step: 0.05, def: 0.5, ls: LS_EXAG,  engine: 'expressive', label: 'Expressiveness' },
    pace:         { min: 0.1, max: 0.9, step: 0.05, def: 0.5, ls: LS_PACE,  engine: 'expressive', label: 'Pace' },
    pitch:        { min: -6,  max: 6,   step: 0.5,  def: 0,   ls: LS_PITCH, engine: 'expressive', label: 'Pitch' },
    variation:    { min: 0.1, max: 1.5, step: 0.05, def: 0.8, ls: LS_VAR,   engine: 'expressive', label: 'Variation' },
};

// The Kokoro set, kept as the offline answer: the picker must still open
// and be usable before /voice/voices replies (or when it cannot). The
// server is the authority — see loadVoices() — and its list replaces this
// one, adding expressive and ElevenLabs voices whenever those lanes are on.
const KOKORO_VOICES = [
    { id: 'af_alloy',   name: 'Alloy',    gender: 'Female', accent: 'American' },
    { id: 'af_aoede',   name: 'Aoede',    gender: 'Female', accent: 'American' },
    { id: 'af_bella',   name: 'Bella',    gender: 'Female', accent: 'American' },
    { id: 'af_heart',   name: 'Heart',    gender: 'Female', accent: 'American', isDefault: true },
    { id: 'af_jessica', name: 'Jessica',  gender: 'Female', accent: 'American' },
    { id: 'af_kore',    name: 'Kore',     gender: 'Female', accent: 'American' },
    { id: 'af_nicole',  name: 'Nicole',   gender: 'Female', accent: 'American' },
    { id: 'af_nova',    name: 'Nova',     gender: 'Female', accent: 'American' },
    { id: 'af_river',   name: 'River',    gender: 'Female', accent: 'American' },
    { id: 'af_sarah',   name: 'Sarah',    gender: 'Female', accent: 'American' },
    { id: 'af_sky',     name: 'Sky',      gender: 'Female', accent: 'American' },
    { id: 'am_adam',    name: 'Adam',     gender: 'Male', accent: 'American' },
    { id: 'am_echo',    name: 'Echo',     gender: 'Male', accent: 'American' },
    { id: 'am_eric',    name: 'Eric',     gender: 'Male', accent: 'American' },
    { id: 'am_fenrir',  name: 'Fenrir',   gender: 'Male', accent: 'American' },
    { id: 'am_liam',    name: 'Liam',     gender: 'Male', accent: 'American' },
    { id: 'am_michael', name: 'Michael',  gender: 'Male', accent: 'American' },
    { id: 'am_onyx',    name: 'Onyx',     gender: 'Male', accent: 'American' },
    { id: 'am_puck',    name: 'Puck',     gender: 'Male', accent: 'American' },
    { id: 'am_santa',   name: 'Santa',    gender: 'Male', accent: 'American' },
    { id: 'bf_alice',    name: 'Alice',    gender: 'Female', accent: 'British' },
    { id: 'bf_emma',     name: 'Emma',     gender: 'Female', accent: 'British' },
    { id: 'bf_isabella', name: 'Isabella', gender: 'Female', accent: 'British' },
    { id: 'bf_lily',     name: 'Lily',     gender: 'Female', accent: 'British' },
    { id: 'bm_daniel',  name: 'Daniel',   gender: 'Male', accent: 'British' },
    { id: 'bm_fable',   name: 'Fable',    gender: 'Male', accent: 'British' },
    { id: 'bm_george',  name: 'George',   gender: 'Male', accent: 'British' },
    { id: 'bm_lewis',   name: 'Lewis',    gender: 'Male', accent: 'British' },
].map(v => ({ ...v, engine: 'kokoro', provider: 'kokoro' }));

let modalOpen = false;
let pendingVoiceId = null;
let modalElements = {};

let VOICES = KOKORO_VOICES;
let voiceMeta = { kokoro_available: true, expressive_available: false, cloud_available: false, credits: null, engine_workers: null };
let serverDefaultVoice = DEFAULT_VOICE;
let profile = null;          // last /voice/speech-profile payload
let profileDirty = false;

function engineOf(voiceId) {
    const v = VOICES.find(x => x.id === voiceId);
    if (v?.engine) return v.engine;
    if (v?.provider === 'elevenlabs') return 'elevenlabs';
    return (voiceId || '').startsWith('xv_') ? 'expressive' : 'kokoro';
}

let designedVoices = new Set();

async function loadVoices() {
    try {
        const res = await fetch(`${API_BASE}/voice/voices`, { headers: getHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data.voices) || !data.voices.length) return;
        voiceMeta = {
            kokoro_available: !!data.kokoro_available,
            expressive_available: !!data.expressive_available,
            cloud_available: !!data.cloud_available,
            credits: data.credits || null,
            engine_workers: data.engine_workers || null,
        };
        if (data.default_voice_id) serverDefaultVoice = data.default_voice_id;
        VOICES = data.voices.map(v => ({ ...v, isDefault: v.id === serverDefaultVoice }));
        // Which of Your voices were saved from the Create tab: removing one
        // of those only takes it off this list, and the card says so.
        try {
            const d = await fetch(`${API_BASE}/voice/designs`, { headers: getHeaders() });
            if (d.ok) designedVoices = new Set(((await d.json()).designs || []).map(x => x.voice_id).filter(Boolean));
        } catch { /* the cards fall back to the plain wording */ }
    } catch {
        // Keep the built-in list; the picker still works with Kokoro.
    }
}

// ─── Container preferences: the server's copy wins over the browser's ──

let prefsLoaded = false;

async function loadPrefs() {
    try {
        const res = await fetch(`${API_BASE}/voice/prefs`, { headers: getHeaders() });
        if (!res.ok) return;
        const { prefs } = await res.json();
        if (!prefs || typeof prefs !== 'object') { prefsLoaded = true; return; }
        // Apply into the localStorage keys the rest of this module reads,
        // so one code path serves both the saved and the unsaved case.
        try {
            if (prefs.voice) localStorage.setItem(LS_KEY, prefs.voice);
            for (const [key, cfg] of Object.entries(SLIDERS)) {
                if (prefs[key] !== undefined && prefs[key] !== null) localStorage.setItem(cfg.ls, String(prefs[key]));
                else localStorage.removeItem(cfg.ls);
            }
        } catch { /* storage blocked: the panel still works for this visit */ }
        prefsLoaded = true;
    } catch { /* offline: keep whatever the browser has */ }
}

let savePrefsTimer = null;
function savePrefs(immediate = false) {
    clearTimeout(savePrefsTimer);
    const run = async () => {
        const voice = localStorage.getItem(LS_KEY) || '';
        const body = { voice };
        for (const [key, cfg] of Object.entries(SLIDERS)) {
            const raw = localStorage.getItem(cfg.ls);
            if (raw !== null) body[key] = parseFloat(raw);
        }
        try {
            await fetch(`${API_BASE}/voice/prefs`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(body) });
        } catch (e) {
            console.warn('[VoiceManager] prefs save failed:', e);
        }
    };
    if (immediate) run(); else savePrefsTimer = setTimeout(run, 400);
}

async function loadProfile() {
    try {
        const res = await fetch(`${API_BASE}/voice/speech-profile`, { headers: getHeaders() });
        if (res.ok) profile = await res.json();
    } catch { /* panel still works without the lists */ }
}

// Preview playback state
let previewAudio = null;
let previewVoiceId = null;
const previewCache = new Map(); // `${voiceId}|${optsKey}` -> blob URL

// ─── Public API ──────────────────────────────────────────────────────

export function getSelectedVoice() {
    // A stored id can outlive the lane that served it: pick a Kokoro voice,
    // switch every worker off, and af_heart names a voice nothing can speak.
    // Same for an expressive voice once its worker is parked.
    const stored = localStorage.getItem(LS_KEY) || serverDefaultVoice;
    if (VOICES.some(v => v.id === stored)) return stored;
    if (VOICES.some(v => v.id === serverDefaultVoice)) return serverDefaultVoice;
    return VOICES.length ? VOICES[0].id : stored;
}

export function getVoiceDisplayName(voiceId) {
    const id = voiceId || getSelectedVoice();
    const v = VOICES.find(v => v.id === id);
    return v ? v.name : (VOICES[0]?.name || 'Heart');
}

/**
 * The speech options every TTS request carries: only the sliders that
 * apply to the selected voice's engine, and only when moved off default.
 */
/** Does this control apply to the engine speaking? 'both' means either. */
function appliesTo(cfg, engine) {
    return cfg.engine === 'both' || cfg.engine === engine;
}

export function getTTSOptions(voiceId) {
    const engine = engineOf(voiceId || getSelectedVoice());
    const out = {};
    for (const [key, cfg] of Object.entries(SLIDERS)) {
        if (!appliesTo(cfg, engine)) continue;
        const v = sliderValue(key);
        if (Math.abs(v - cfg.def) > 1e-9) out[key] = v;
    }
    return out;
}

export function initVoiceManager() {
    createModalHTML();
    Promise.all([loadPrefs(), loadVoices()]).then(updateDisplayButton);
    const $ = id => document.getElementById(id);
    modalElements = {
        overlay: $('voice-manager-overlay'),
        panel: $('voice-manager-panel'),
        closeBtn: $('voice-manager-close'),
        maximizeBtn: $('voice-manager-maximize'),
        tabVoices: $('voice-tab-voices'),
        tabCreate: $('voice-tab-create'),
        minimizeBtn: $('voice-manager-minimize'),
        searchInput: $('voice-search'),
        searchRow: $('voice-search-row'),
        searchToggle: $('voice-search-toggle'),
        enginePills: $('voice-engine-pills'),
        voiceList: $('voice-list'),
        selectBtn: $('voice-select-btn'),
        resetBtn: $('voice-reset-btn'),
        settings: $('voice-settings'),
    };
    modalElements.closeBtn?.addEventListener('click', closeVoiceManager);
    // Each button is lit only when it has somewhere to go, as on the message
    // editor and the KB panel.
    const setMaximized = (on) => {
        modalElements.panel?.classList.toggle('maximized', on);
        if (modalElements.maximizeBtn) modalElements.maximizeBtn.disabled = on;
        if (modalElements.minimizeBtn) modalElements.minimizeBtn.disabled = !on;
    };
    // Create: describe a voice and generate it. A voice made or renamed there
    // has to appear here without reopening the panel.
    initVoiceDesigner(modalElements.tabCreate, {
        onVoicesChanged: () => loadVoices().then(() => {
            if (!modalOpen) return;
            renderEngineBar();
            renderVoiceList(modalElements.searchInput?.value || '');
        }),
    });
    modalElements.panel?.querySelector('.voice-tabs')?.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-tab]')?.dataset.tab;
        if (tab) showTab(tab);
    });
    modalElements.maximizeBtn?.addEventListener('click', () => setMaximized(true));
    modalElements.minimizeBtn?.addEventListener('click', () => setMaximized(false));
    modalElements.overlay?.addEventListener('click', closeVoiceManager);
    modalElements.selectBtn?.addEventListener('click', handleSelect);
    modalElements.resetBtn?.addEventListener('click', handleReset);
    modalElements.searchInput?.addEventListener('input', () => renderVoiceList(modalElements.searchInput.value));
    modalElements.searchToggle?.addEventListener('click', toggleSearch);
    modalElements.enginePills?.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-engine]');
        if (!pill) return;
        engineFilter = pill.dataset.engine;
        renderEngineBar();
        renderVoiceList(modalElements.searchInput?.value || '');
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) closeVoiceManager();
    });
    wireSettings();
    updateDisplayButton();
}

export function openVoiceManager() {
    if (modalOpen) return;
    modalOpen = true;
    pendingVoiceId = getSelectedVoice();
    modalElements.overlay?.classList.add('active');
    modalElements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';
    if (modalElements.searchInput) modalElements.searchInput.value = '';
    if (modalElements.searchRow) modalElements.searchRow.hidden = true;
    modalElements.searchToggle?.classList.remove('active');
    modalElements.searchToggle?.setAttribute('aria-expanded', 'false');
    renderEngineBar();
    renderVoiceList('');
    renderSettings();
    if (activeTab === 'create') showVoiceDesigner();
    // Which voices exist can change between openings (a worker switched
    // off, the expressive chip switched on), so ask every time.
    Promise.all([loadVoices(), loadProfile(), prefsLoaded ? null : loadPrefs()]).then(() => {
        if (!modalOpen) return;
        pendingVoiceId = pendingVoiceId || getSelectedVoice();
        renderEngineBar();
        renderVoiceList(modalElements.searchInput?.value || '');
        renderSettings();
    });
}

// ─── Internal ────────────────────────────────────────────────────────

let activeTab = 'voices';

/** Voices (pick and tune) or Create (describe and generate). */
function showTab(tab) {
    activeTab = tab;
    modalElements.panel?.querySelectorAll('.voice-tab-btn').forEach((b) => {
        const on = b.dataset.tab === tab;
        b.classList.toggle('active', on);
        b.setAttribute('aria-selected', String(on));
    });
    if (modalElements.tabVoices) modalElements.tabVoices.hidden = tab !== 'voices';
    if (modalElements.tabCreate) modalElements.tabCreate.hidden = tab !== 'create';
    stopPreview();
    if (tab === 'create') showVoiceDesigner();
    else hideVoiceDesigner();
}

function closeVoiceManager() {
    if (!modalOpen) return;
    modalOpen = false;
    pendingVoiceId = null;
    stopPreview();
    hideVoiceDesigner();
    modalElements.overlay?.classList.remove('active');
    modalElements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

function handleSelect() {
    if (!pendingVoiceId) return;
    localStorage.setItem(LS_KEY, pendingVoiceId);
    savePrefs(true);
    updateDisplayButton();
    closeVoiceManager();
}

function handleReset() {
    pendingVoiceId = serverDefaultVoice;
    localStorage.setItem(LS_KEY, serverDefaultVoice);
    for (const cfg of Object.values(SLIDERS)) localStorage.removeItem(cfg.ls);
    localStorage.removeItem(LS_KEY);
    fetch(`${API_BASE}/voice/prefs`, { method: 'PUT', headers: getHeaders(), body: '{}' }).catch(() => {});
    updateDisplayButton();
    renderVoiceList(modalElements.searchInput?.value || '');
    renderSettings();
    closeVoiceManager();
}

function updateDisplayButton() {
    const display = document.getElementById('current-voice-display');
    if (display) display.textContent = getVoiceDisplayName();
    const btn = document.getElementById('voice-selector-btn');
    if (btn) {
        const current = getSelectedVoice();
        const tuned = Object.keys(getTTSOptions(current)).length > 0;
        btn.classList.toggle('has-selection', current !== serverDefaultVoice || tuned);
    }
}

/**
 * Engines a listener can actually reach right now.
 *
 * Offering a Kokoro voice when every worker is set to Expressive is a promise
 * the pool cannot keep: picking one gets silence or a switch nobody asked for.
 * Falls open when the board reports nothing, so a router that has not answered
 * yet does not empty the picker.
 */
function liveEngines() {
    const ew = voiceMeta.engine_workers;
    if (!ew) return null;
    const live = new Set();
    if ((ew.kokoro || []).length) live.add('kokoro');
    if ((ew.expressive || []).length) live.add('expressive');
    if (!live.size) return null;
    // The cloud lane is not a worker; it stands or falls on its own switch.
    if (voiceMeta.cloud_available) live.add('elevenlabs');
    return live;
}

function renderVoiceList(searchTerm) {
    const container = modalElements.voiceList;
    if (!container) return;
    const term = (searchTerm || '').toLowerCase();
    const currentVoice = getSelectedVoice();
    const live = liveEngines();
    ensureEngineFilter();

    const groups = {};
    for (const v of VOICES) {
        const eng = v.engine || v.provider;
        if (engineFilter) {
            // One engine at a time: a voice belongs to exactly one, and
            // listing every engine at once meant the same names twice over.
            if (eng !== engineFilter) continue;
        } else if (live && !live.has(eng) && v.id !== currentVoice) {
            // Nothing to filter by yet (the board has not answered). Keep the
            // voice in use visible even if its engine just went away, so the
            // panel explains the state instead of losing the selection.
            continue;
        }
        const hay = `${v.name} ${v.id} ${v.gender || ''} ${v.accent || ''} ${v.description || ''} ${v.engine || ''}`.toLowerCase();
        if (term && !hay.includes(term)) continue;
        const key = (`${v.accent || ''} ${v.gender || ''}`).trim()
            || (v.provider === 'elevenlabs' ? 'ElevenLabs' : 'Other');
        (groups[key] ||= []).push(v);
    }
    if (Object.keys(groups).length === 0) {
        container.innerHTML = '<div class="context-manager-empty">No voices match your search</div>';
        return;
    }
    let html = '';
    for (const [groupName, voices] of Object.entries(groups)) {
        const engines = [...new Set(voices.map(v => v.engine || v.provider))];
        const tag = engines.length === 1 && engines[0] === 'expressive'
            ? '<span class="voice-group-tag expressive">expressive</span>'
            : engines.length === 1 && engines[0] === 'kokoro' ? '<span class="voice-group-tag">kokoro</span>' : '';
        html += `<div class="voice-group-header">${groupName}${tag}<span class="voice-group-count">${voices.length}</span></div>`;
        html += '<div class="voice-card-grid">';
        for (const v of voices) {
            const isSelected = v.id === pendingVoiceId;
            const isCurrent = v.id === currentVoice;
            const isPlaying = v.id === previewVoiceId;
            const meta = v.engine === 'expressive'
                ? (v.id === 'xv_default' ? 'Built-in speaker' : 'Expressive take')
                : v.provider === 'elevenlabs' ? 'ElevenLabs' : 'Kokoro';
            html += `
                <div class="voice-card${isSelected ? ' selected' : ''}" data-voice-id="${v.id}" title="${escAttr(v.description || '')}">
                    <button class="voice-preview-btn${isPlaying ? ' playing' : ''}" data-voice-id="${v.id}" data-voice-name="${escAttr(v.name)}" title="Preview">
                        <svg class="voice-preview-play" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                            <polygon points="6 3 20 12 6 21 6 3"/>
                        </svg>
                        <svg class="voice-preview-stop" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                            <rect x="6" y="6" width="12" height="12" rx="1"/>
                        </svg>
                        <div class="voice-preview-spinner"></div>
                    </button>
                    <span class="voice-card-text">
                        <span class="voice-card-name">${v.name}</span>
                        <span class="voice-card-meta">${meta}</span>
                    </span>
                    <div class="voice-card-badges">
                        ${v.isDefault ? '<span class="voice-badge default" title="Default voice">★</span>' : ''}
                        ${isCurrent && !isSelected ? '<span class="voice-badge current">CURRENT</span>' : ''}
                        ${v.custom ? `<button class="voice-card-del" data-del-voice="${v.id}" title="${designedVoices.has(v.id) ? 'Remove from Your voices. It stays in Create, where you can save it again.' : 'Delete this voice'}">×</button>` : ''}
                    </div>
                </div>`;
        }
        if (groupName === 'Your voices') html += addVoiceCard();
        html += '</div>';
    }
    const showingExpressive = !engineFilter || engineFilter === 'expressive';
    if (showingExpressive && voiceMeta.expressive_available && !groups['Your voices']) {
        html += '<div class="voice-group-header">Your voices<span class="voice-group-tag expressive">expressive</span></div>'
              + `<div class="voice-card-grid">${addVoiceCard()}</div>`;
    }
    if (showingExpressive && !voiceMeta.expressive_available) {
        html += '<div class="voice-lane-note">Expressive takes of these voices appear when a worker\'s speech chip '
              + 'is switched to Expressive on the switchboard.</div>';
    }
    container.innerHTML = html;

    container.querySelectorAll('.voice-card').forEach(card => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('.voice-preview-btn')) return;
            pendingVoiceId = card.dataset.voiceId;
            container.querySelectorAll('.voice-card.selected').forEach(c => c.classList.remove('selected'));
            card.classList.add('selected');
            modalElements.selectBtn.disabled = false;
            renderSettings();
        });
    });
    container.querySelectorAll('[data-del-voice]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const id = btn.dataset.delVoice;
            const v = VOICES.find(x => x.id === id);
            const designed = designedVoices.has(id);
            if (!designed && !confirm(`Delete the voice "${v?.name || id}"? Workers forget it too.`)) return;
            try {
                const res = await fetch(`${API_BASE}/voice/custom-voices/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getHeaders() });
                if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
                if (pendingVoiceId === id) pendingVoiceId = null;
                if (localStorage.getItem(LS_KEY) === id) localStorage.removeItem(LS_KEY);
                await loadVoices();
                renderVoiceList(modalElements.searchInput?.value || '');
                renderSettings();
                updateDisplayButton();
            } catch (err) {
                alert(`Could not delete: ${err.message}`);
            }
        });
    });
    container.querySelector('[data-add-voice]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openAddVoice();
    });
    container.querySelectorAll('.voice-preview-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const voiceId = btn.dataset.voiceId;
            if (previewVoiceId === voiceId) stopPreview();
            else playPreview(voiceId, btn.dataset.voiceName, `Hi, I'm ${btn.dataset.voiceName}. How can I help you today?`);
        });
    });
    modalElements.selectBtn.disabled = !pendingVoiceId;
}

function addVoiceCard() {
    return `<button class="voice-card voice-card-add" data-add-voice title="Clone a voice from a recording or a file">
        <span class="voice-card-plus">+</span>
        <span class="voice-card-text"><span class="voice-card-name">Add a voice</span><span class="voice-card-meta">record or upload</span></span>
    </button>`;
}

// ─── Add a voice: record or upload, name, scope ──────────────────────

let recorder = null;
let recChunks = [];
let recTimer = null;
let recBlob = null;
const REC_MAX_S = 20;

function openAddVoice() {
    const root = modalElements.settings;
    if (!root) return;
    recBlob = null;
    const canShare = !state.containerMode;
    root.innerHTML = `
        <div class="voice-card-section voice-add">
            <div class="voice-section-head"><span>Add a voice</span>
                <span class="voice-section-sub">${escHtml(String(6))}–${REC_MAX_S} seconds of one person speaking naturally, no music</span></div>
            <div class="voice-add-row">
                <input type="text" id="voice-add-name" maxlength="48" placeholder="Name this voice (e.g. Alex)">
                <select id="voice-add-scope" ${canShare ? '' : 'disabled title="Only an administrator can share a voice with everyone"'}>
                    <option value="container" ${canShare ? '' : 'selected'}>${canShare ? 'Only here' : 'This container'}</option>
                    ${canShare ? '<option value="global" selected>Everyone</option>' : ''}
                </select>
            </div>
            <div class="voice-add-row voice-add-sources">
                <button class="btn-secondary" data-act="rec-toggle" id="voice-rec-btn">● Record</button>
                <span id="voice-rec-status" class="voice-words-hint">or</span>
                <label class="btn-secondary voice-file-label">Choose a file<input type="file" id="voice-add-file" accept="audio/*,video/webm" hidden></label>
                <span id="voice-add-clip" class="voice-words-hint"></span>
            </div>
            <div class="voice-words-hint">Container-scoped voices are stored under that container's encryption. The clip is sent to every worker running the expressive engine.</div>
            <div class="voice-words-actions">
                <button class="btn-secondary" data-act="add-cancel">Cancel</button>
                <button class="menu-btn primary" data-act="add-save" id="voice-add-save" disabled>Save voice</button>
            </div>
        </div>`;
    root.querySelector('#voice-add-file')?.addEventListener('change', (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        stopRecording(false);
        recBlob = f;
        setClipStatus(`${f.name} · ${(f.size / 1024).toFixed(0)}KB`);
    });
    root.querySelector('#voice-add-name')?.focus();
}

function setClipStatus(text) {
    const el = modalElements.settings?.querySelector('#voice-add-clip');
    if (el) el.textContent = text;
    const save = modalElements.settings?.querySelector('#voice-add-save');
    if (save) save.disabled = !recBlob;
}

async function toggleRecording() {
    if (recorder) { stopRecording(true); return; }
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        recChunks = [];
        recorder = new MediaRecorder(stream);
        recorder.ondataavailable = (e) => { if (e.data.size) recChunks.push(e.data); };
        recorder.onstop = () => {
            stream.getTracks().forEach(t => t.stop());
            recBlob = new Blob(recChunks, { type: recorder.mimeType || 'audio/webm' });
            recorder = null;
            const btn = modalElements.settings?.querySelector('#voice-rec-btn');
            if (btn) btn.textContent = '● Record again';
            setClipStatus(`recorded ${((recBlob.size || 0) / 1024).toFixed(0)}KB`);
        };
        recorder.start();
        const t0 = Date.now();
        const btn = modalElements.settings?.querySelector('#voice-rec-btn');
        if (btn) btn.textContent = '■ Stop';
        recTimer = setInterval(() => {
            const s = Math.round((Date.now() - t0) / 1000);
            const st = modalElements.settings?.querySelector('#voice-rec-status');
            if (st) st.textContent = `recording… ${s}s`;
            if (s >= REC_MAX_S) stopRecording(true);
        }, 250);
    } catch (e) {
        alert(`Microphone not available: ${e.message}`);
    }
}

function stopRecording(keep) {
    clearInterval(recTimer); recTimer = null;
    const st = modalElements.settings?.querySelector('#voice-rec-status');
    if (st) st.textContent = 'or';
    if (recorder && recorder.state !== 'inactive') {
        if (!keep) recorder.ondataavailable = null;
        recorder.stop();
    }
    if (!keep) recorder = null;
}

async function saveNewVoice(btn) {
    const root = modalElements.settings;
    const name = (root.querySelector('#voice-add-name')?.value || '').trim();
    const scope = root.querySelector('#voice-add-scope')?.value || 'container';
    if (!name) { root.querySelector('#voice-add-name')?.focus(); return; }
    if (!recBlob) return;
    btn.disabled = true; btn.textContent = 'Saving…';
    const fd = new FormData();
    fd.append('name', name);
    fd.append('scope', scope);
    fd.append('file', recBlob, recBlob.name || 'clip.webm');
    try {
        const res = await fetch(`${API_BASE}/voice/custom-voices`, { method: 'POST', headers: getHeaders(false), body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        await loadVoices();
        pendingVoiceId = data.voice?.id || pendingVoiceId;
        renderVoiceList(modalElements.searchInput?.value || '');
        renderSettings();
        if (modalElements.selectBtn) modalElements.selectBtn.disabled = !pendingVoiceId;
    } catch (e) {
        alert(`Could not add the voice: ${e.message}`);
        btn.disabled = false; btn.textContent = 'Save voice';
    }
}

// ─── Settings: sliders + word lists ──────────────────────────────────

function sliderValue(key) {
    const cfg = SLIDERS[key];
    try {
        const raw = localStorage.getItem(cfg.ls);
        const v = raw === null ? cfg.def : parseFloat(raw);
        return Number.isFinite(v) ? Math.min(cfg.max, Math.max(cfg.min, v)) : cfg.def;
    } catch { return cfg.def; }
}

function wireSettings() {
    const root = modalElements.settings;
    if (!root) return;
    root.addEventListener('input', (e) => {
        const key = e.target.dataset?.slider;
        if (key && SLIDERS[key]) {
            const v = parseFloat(e.target.value);
            if (Math.abs(v - SLIDERS[key].def) < 1e-9) localStorage.removeItem(SLIDERS[key].ls);
            else localStorage.setItem(SLIDERS[key].ls, String(v));
            const out = root.querySelector(`[data-slider-value="${key}"]`);
            if (out) out.textContent = formatSlider(key, v);
            updateDisplayButton();
            savePrefs();
        }
        if (e.target.matches('textarea[data-list]')) profileDirty = true;
        if (e.target.id === 'voice-say-input') {
            try { localStorage.setItem(SAY_TEXT_LS, e.target.value); } catch { /* private mode */ }
        }
    });
    root.addEventListener('click', async (e) => {
        const act = e.target.closest('[data-act]')?.dataset.act;
        if (!act) return;
        if (act === 'say-it') {
            // The same button stops what it started.
            if (sayAbort || sayPlaying) { stopPreview(); return; }
            const input = root.querySelector('#voice-say-input');
            // Only the ends are trimmed: the line breaks inside are pauses.
            const text = (input?.value || '').replace(/^\s+|\s+$/g, '');
            if (!text) return;
            stopPreview();
            sayIt(pendingVoiceId || getSelectedVoice(), text);
        } else if (act === 'save-profile') {
            await saveProfile(e.target.closest('[data-act]'));
        } else if (act === 'reset-profile') {
            if (!confirm('Forget this scope\'s saved words and go back to the built-in lists?')) return;
            await putProfile({ reset: true });
        } else if (act === 'rec-toggle') {
            await toggleRecording();
        } else if (act === 'add-cancel') {
            stopRecording(false);
            recBlob = null;
            renderSettings();
        } else if (act === 'add-save') {
            await saveNewVoice(e.target.closest('[data-act]'));
        } else if (act === 'toggle-words') {
            const body = root.querySelector('#voice-words-body');
            body.hidden = !body.hidden;
            renderSettings();
        }
    });
}

function formatSlider(key, v) {
    if (key === 'speed') return `${v.toFixed(2)}×`;
    if (key === 'pitch') return `${v > 0 ? '+' : ''}${v.toFixed(1)} st`;
    // A percentage is where the handle SITS, not the raw number. Pace runs
    // 0.1 to 1.0 and expressiveness 0 to 1, so reading the value out meant
    // two sliders both saying 50% with their handles in different places.
    const cfg = SLIDERS[key];
    if (!cfg) return `${Math.round(v * 100)}%`;
    return `${Math.round(((v - cfg.min) / (cfg.max - cfg.min)) * 100)}%`;
}

function renderSettings() {
    const root = modalElements.settings;
    if (!root) return;
    const voiceId = pendingVoiceId || getSelectedVoice();
    const engine = engineOf(voiceId);
    const wordsOpen = root.querySelector('#voice-words-body') ? !root.querySelector('#voice-words-body').hidden : false;
    const sayText = root.querySelector('#voice-say-input')?.value ?? savedSayText();

    const HINTS = {
        speed: 'How fast the voice reads. 1.00× is natural. Pace slows or quickens on top of this.',
        exaggeration: 'How much feeling Chatterbox puts in. Half is even; higher is animated.',
        pace: 'How unhurried the delivery is. Lowest reads at about 0.7× and highest at 1.3×, on top of Speed. On the full model, lower is also looser and more dramatic.',
        pitch: 'Deeper or higher, without changing how long it takes to say.',
        variation: 'How much the delivery varies between takes. Lower is steadier and sticks closer to the words; higher is more spontaneous and occasionally invents a sound.',
    };
    let html = '<div class="voice-card-section"><div class="voice-section-head"><span>Speech settings</span>'
          + `<span class="voice-section-sub">for ${escHtml(getVoiceDisplayName(voiceId))}</span></div>`;
    html += '<div class="voice-speech-grid"><div class="voice-sliders">';
    // Which sliders this voice's engine ignores, said once under the column
    // instead of as a tag on every row: the column is half as wide now.
    const idle = {};
    for (const [key, cfg] of Object.entries(SLIDERS)) {
        const on = appliesTo(cfg, engine);
        const v = sliderValue(key);
        if (!on) (idle[cfg.engine] = idle[cfg.engine] || []).push(cfg.label);
        html += `
            <label class="voice-setting-row${on ? '' : ' disabled'}" title="${escAttr(HINTS[key])}">
                <span class="voice-setting-label">${cfg.label}</span>
                <input type="range" data-slider="${key}" min="${cfg.min}" max="${cfg.max}" step="${cfg.step}" value="${v}" ${on ? '' : 'disabled'}>
                <span class="voice-setting-value" data-slider-value="${key}">${formatSlider(key, v)}</span>
            </label>`;
    }
    const list = (names) => names.length > 1
        ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
    for (const [eng, names] of Object.entries(idle)) {
        html += `<p class="voice-sliders-note">${escHtml(list(names))} ${names.length > 1 ? 'apply' : 'applies'} to ${eng === 'expressive' ? 'expressive' : 'Kokoro'} voices only.</p>`;
    }
    html += `
        </div>
        <div class="voice-say">
            <textarea id="voice-say-input" class="voice-say-input" rows="6" spellcheck="false"
                aria-label="Text to hear in this voice"
                placeholder="Type or paste something to hear it in this voice. Line breaks are pauses, exactly as in a reply read aloud.">${escHtml(sayText)}</textarea>
            <div class="voice-say-actions">
                <span class="voice-say-status" id="voice-say-status" role="status" aria-live="polite">${escHtml(sayStatus)}</span>
                <button class="btn-secondary voice-say-btn" data-act="say-it" title="Speak this with the selected voice and settings, the way a reply is read aloud">${sayAbort || sayPlaying ? 'Stop' : 'Say it'}</button>
            </div>
        </div>
    </div>
    </div>
    <div class="voice-card-section voice-words-section">
        <button class="voice-section-head voice-words-toggle" data-act="toggle-words" aria-expanded="${wordsOpen}">
            <span>Pronunciations &amp; vocabulary</span>
            <span class="voice-section-sub">teach the ear and the mouth</span>
            <span class="voice-chevron">${wordsOpen ? '▾' : '▸'}</span>
        </button>
        <div id="voice-words-body" ${wordsOpen ? '' : 'hidden'}>${renderWords()}</div>
    </div>`;
    root.innerHTML = html;
}

/**
 * Which engine's voices the list is showing.
 *
 * With one worker on Kokoro and another on Expressive, both engines are live
 * and the picker used to list every voice twice over. A voice belongs to one
 * engine, so the list belongs to one engine too; this is the choice, and it
 * starts on whatever the current voice already uses.
 */
let engineFilter = null;

function engineChoices() {
    const ew = voiceMeta.engine_workers;
    if (!ew) return [];
    const out = [];
    if (ew.expressive?.length) {
        out.push({ id: 'expressive', label: 'Expressive', where: `Chatterbox · ${ew.expressive.join(', ')}` });
    }
    if (ew.kokoro?.length) out.push({ id: 'kokoro', label: 'Kokoro', where: ew.kokoro.join(', ') });
    if (voiceMeta.cloud_available) out.push({ id: 'elevenlabs', label: 'ElevenLabs', where: 'cloud lane' });
    return out;
}

/** Settle the filter on something that exists: the current voice's engine first. */
function ensureEngineFilter() {
    const choices = engineChoices();
    if (!choices.length) {
        engineFilter = null;
        return;
    }
    if (choices.some(c => c.id === engineFilter)) return;
    const mine = engineOf(pendingVoiceId || getSelectedVoice());
    engineFilter = choices.some(c => c.id === mine) ? mine : choices[0].id;
}

/**
 * The engine row: what the switchboard set, and which of them you are
 * browsing. The board's speech chip decides what each worker speaks with;
 * this only follows it, so the machine names live in the tooltip rather than
 * taking a line of the panel.
 */
function renderEngineBar() {
    const root = modalElements.enginePills;
    if (!root) return;
    ensureEngineFilter();
    const bar = root.parentElement;
    // Null means the board has not answered yet, which is not the same as
    // nothing being switched on. Saying "No speaker" while it loads would be
    // a false alarm, so the row simply stays out of the way until we know.
    if (!voiceMeta.engine_workers) {
        root.innerHTML = '';
        if (bar) bar.hidden = true;
        return;
    }
    if (bar) bar.hidden = false;
    const choices = engineChoices();
    if (!choices.length) {
        root.innerHTML = '<span class="voice-engine-pill off" title="Every voice worker is switched off on the switchboard">No speaker</span>';
        return;
    }
    root.innerHTML = choices.map(c =>
        `<button type="button" class="voice-engine-pill ${c.id}${c.id === engineFilter ? ' active' : ''}"`
        + ` data-engine="${c.id}" aria-pressed="${c.id === engineFilter}"`
        + ` title="${escAttr(`${c.where} — set on the switchboard; click to see its voices`)}">${c.label}</button>`
    ).join('');
}

/** The search box is out of the way until it is wanted: the list is the point. */
function toggleSearch() {
    const row = modalElements.searchRow;
    if (!row) return;
    const show = row.hidden;
    row.hidden = !show;
    modalElements.searchToggle?.setAttribute('aria-expanded', String(show));
    modalElements.searchToggle?.classList.toggle('active', show);
    if (show) {
        modalElements.searchInput?.focus();
    } else if (modalElements.searchInput?.value) {
        modalElements.searchInput.value = '';
        renderVoiceList('');
    }
}

function renderWords() {
    if (!profile) return '<div class="voice-lane-note">Loading word lists…</div>';
    const scopeNote = profile.scope === 'container'
        ? `This container's lists, on top of ${profile.global_lexicon.length} shared pronunciations and ${profile.global_vocab.length} shared terms.`
        : 'Shared lists: every container inherits these.';
    const lex = profile.lexicon.map(e => `${e.word} = ${e.say || ''}${e.phonemes ? ` | ${e.phonemes}` : ''}`).join('\n');
    const voc = profile.vocab.map(e => `${e.term}${e.aliases?.length ? ` = ${e.aliases.join(', ')}` : ''}`).join('\n');
    return `
        <div class="voice-words-grid">
            <div class="voice-words-col">
                <div class="voice-words-head"><span>Pronunciations</span><span class="voice-tag">speech</span></div>
                <div class="voice-words-hint">One per line: <code>word = how to say it</code>, optionally <code>| IPA</code> for an exact reading.</div>
                <textarea data-list="lexicon" rows="7" spellcheck="false" placeholder="jalapeño = hala-payn-yo | hɑləpˈeɪnjO">${escHtml(lex)}</textarea>
            </div>
            <div class="voice-words-col">
                <div class="voice-words-head"><span>Vocabulary</span><span class="voice-tag">listening</span></div>
                <div class="voice-words-hint">One per line: <code>Term = what it gets misheard as, …</code>. Terms are hinted to the transcriber; mishearings are corrected.</div>
                <textarea data-list="vocab" rows="7" spellcheck="false" placeholder="Claude Code = cloud code, clod code">${escHtml(voc)}</textarea>
            </div>
        </div>
        <div class="voice-words-actions">
            <span class="voice-words-scope">${scopeNote}</span>
            <button class="btn-secondary" data-act="reset-profile" title="Back to the built-in lists for this scope">Reset</button>
            <button class="menu-btn primary" data-act="save-profile">Save words</button>
        </div>`;
}

function parseLexicon(text) {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
        const [word, rest = ''] = l.split('=').map(s => s.trim());
        const [say = '', phonemes = ''] = rest.split('|').map(s => s.trim());
        return { word, say, phonemes };
    }).filter(e => e.word && (e.say || e.phonemes));
}

function parseVocab(text) {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
        const [term, rest = ''] = l.split('=').map(s => s.trim());
        return { term, aliases: rest ? rest.split(',').map(s => s.trim()).filter(Boolean) : [] };
    }).filter(e => e.term);
}

async function saveProfile(btn) {
    const root = modalElements.settings;
    const lexicon = parseLexicon(root.querySelector('textarea[data-list="lexicon"]')?.value || '');
    const vocab = parseVocab(root.querySelector('textarea[data-list="vocab"]')?.value || '');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
    await putProfile({ lexicon, vocab });
}

async function putProfile(body) {
    try {
        const res = await fetch(`${API_BASE}/voice/speech-profile`, {
            method: 'PUT',
            headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (res.ok) { profile = await res.json(); profileDirty = false; previewCache.clear(); }
    } catch (e) {
        console.error('[VoiceManager] profile save failed:', e);
    }
    renderSettings();
}

// ─── Voice Preview ───────────────────────────────────────────────────

async function playPreview(voiceId, voiceName, text, btnEl = null) {
    stopPreview();
    previewVoiceId = voiceId;
    updatePreviewButton(voiceId, 'loading');
    if (btnEl) { btnEl.disabled = true; btnEl.textContent = '…'; }
    const opts = getTTSOptions(voiceId);
    const cacheKey = `${voiceId}|${text}|${JSON.stringify(opts)}`;
    try {
        let blobUrl = previewCache.get(cacheKey);
        const meta = VOICES.find(v => v.id === voiceId);
        // ElevenLabs ships a hosted sample per voice: auditioning should not
        // spend credits. Only for the stock greeting, not for "Say it".
        if (!blobUrl && meta?.preview_url && !btnEl) {
            previewAudio = new Audio(meta.preview_url);
            previewAudio.addEventListener('ended', () => stopPreview());
            previewAudio.addEventListener('error', () => stopPreview());
            updatePreviewButton(voiceId, 'playing');
            await previewAudio.play();
            return;
        }
        if (!blobUrl) {
            const res = await fetch(`${API_BASE}/voice/tts`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ text, voice: voiceId, ...opts }),
            });
            if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
            const blob = await res.blob();
            blobUrl = URL.createObjectURL(blob);
            previewCache.set(cacheKey, blobUrl);
        }
        if (previewVoiceId !== voiceId) return;
        previewAudio = new Audio(blobUrl);
        previewAudio.addEventListener('ended', () => {
            if (previewVoiceId === voiceId) {
                previewVoiceId = null;
                updatePreviewButton(voiceId, 'idle');
            }
        });
        previewAudio.play();
        updatePreviewButton(voiceId, 'playing');
    } catch (e) {
        console.error('[VoiceManager] Preview failed:', e);
        if (previewVoiceId === voiceId) {
            previewVoiceId = null;
            updatePreviewButton(voiceId, 'idle');
        }
    } finally {
        if (btnEl) { btnEl.disabled = false; btnEl.textContent = 'Say it'; }
    }
}

const SAY_TEXT_LS = 'voice_say_text';
const SAY_DEFAULT = 'The road ran north past the old mill, where the river bends and the water slows.\n\n'
    + 'We stopped there a while and watched the evening come in. "Is anyone there?" Nobody answered.';
let sayAbort = null;      // a render in flight
let sayPlaying = false;   // its audio is playing
let sayStatus = '';

function savedSayText() {
    try { return localStorage.getItem(SAY_TEXT_LS) ?? SAY_DEFAULT; } catch { return SAY_DEFAULT; }
}

function setSayState(status) {
    sayStatus = status;
    const root = modalElements.settings;
    const line = root?.querySelector('#voice-say-status');
    if (line) line.textContent = status;
    const btn = root?.querySelector('.voice-say-btn');
    if (btn) btn.textContent = sayAbort || sayPlaying ? 'Stop' : 'Say it';
}

/** "Say it": the box's text, spoken through the chat reply's own route. */
async function sayIt(voiceId, text) {
    const opts = getTTSOptions(voiceId);
    const cacheKey = `say|${voiceId}|${text}|${JSON.stringify(opts)}`;
    const mine = new AbortController();
    sayAbort = mine;
    previewVoiceId = voiceId;
    setSayState('Generating audio');
    try {
        let url = previewCache.get(cacheKey);
        if (!url) {
            url = await renderSpeechUrl(text, voiceId, opts, {
                signal: mine.signal,
                onProgress: (line) => { if (sayAbort === mine) setSayState(line); },
            });
            previewCache.set(cacheKey, url);
        }
        if (sayAbort !== mine) return;
        sayAbort = null;
        sayPlaying = true;
        const audio = new Audio(url);
        previewAudio = audio;
        const done = (note) => {
            // Stopped or replaced: whoever did that owns the status line now.
            if (previewAudio !== audio) return;
            previewAudio = null;
            sayPlaying = false;
            previewVoiceId = null;
            setSayState(note);
        };
        previewAudio.addEventListener('ended', () => done(''));
        previewAudio.addEventListener('error', () => { previewCache.delete(cacheKey); done('Could not play that audio'); });
        setSayState(`Playing as ${getVoiceDisplayName(voiceId)}`);
        await audio.play();
    } catch (e) {
        if (sayAbort === mine) sayAbort = null;
        sayPlaying = false;
        if (e.name === 'AbortError') return;
        console.error('[VoiceManager] Say it failed:', e);
        setSayState(`Could not generate audio: ${e.message || e}`);
    }
}

function stopPreview() {
    if (sayAbort) { sayAbort.abort(); sayAbort = null; }
    if (sayPlaying || sayStatus) { sayPlaying = false; setSayState(''); }
    if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = '';
        previewAudio = null;
    }
    const prevId = previewVoiceId;
    previewVoiceId = null;
    if (prevId) updatePreviewButton(prevId, 'idle');
}

function updatePreviewButton(voiceId, state) {
    const btn = modalElements.voiceList?.querySelector(`.voice-preview-btn[data-voice-id="${voiceId}"]`);
    if (!btn) return;
    btn.classList.remove('loading', 'playing');
    if (state === 'loading') btn.classList.add('loading');
    else if (state === 'playing') btn.classList.add('playing');
}

function escHtml(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s) {
    return escHtml(s).replace(/"/g, '&quot;');
}

// ─── Modal HTML ──────────────────────────────────────────────────────

function createModalHTML() {
    const html = `
        <div id="voice-manager-overlay" class="modal-overlay"></div>
        <div id="voice-manager-panel" class="context-manager-panel voice-panel">
            <div class="context-manager-header">
                <h2>Voice &amp; Speech</h2>
                <div class="msg-editor-window-btns voice-window-btns">
                    <button id="voice-manager-maximize" class="msg-editor-btn" title="Maximize" aria-label="Maximize">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M16 21h3a2 2 0 0 0 2-2v-3M8 21H5a2 2 0 0 1-2-2v-3"/>
                        </svg>
                    </button>
                    <button id="voice-manager-minimize" class="msg-editor-btn" title="Restore to normal size" aria-label="Restore to normal size" disabled>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M4 10h4V6M20 10h-4V6M4 14h4v4M20 14h-4v4"/>
                        </svg>
                    </button>
                    <button id="voice-manager-close" class="msg-editor-btn" title="Close" aria-label="Close">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div class="voice-tabs" role="tablist" aria-label="Voice and speech">
                <button class="voice-tab-btn active" role="tab" aria-selected="true" data-tab="voices">Voices</button>
                <button class="voice-tab-btn" role="tab" aria-selected="false" data-tab="create">Create</button>
            </div>
            <div id="voice-tab-voices" class="voice-tab" role="tabpanel">
            <div class="voice-engine-bar">
                <span class="voice-board-label">Speaking with</span>
                <span id="voice-engine-pills"></span>
                <button id="voice-search-toggle" class="voice-search-toggle" title="Search voices" aria-label="Search voices" aria-expanded="false">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                </button>
            </div>
            <div class="context-manager-search" id="voice-search-row" hidden>
                <input type="text" id="voice-search" placeholder="Search voices...">
            </div>
            <div id="voice-list" class="context-manager-list"></div>
            <div id="voice-settings" class="voice-settings"></div>
            <div class="context-manager-footer voice-footer">
                <button id="voice-reset-btn" class="btn-secondary">Reset Default</button>
                <button id="voice-select-btn" class="menu-btn primary" disabled>Use this voice</button>
            </div>
            </div>
            <div id="voice-tab-create" class="voice-tab voice-designer" role="tabpanel" hidden></div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', html);
}
